<template>
  <div class="row">
    <div class="col-md-3">
      <form class="needs-validation" ref="form" id="form">
        <div class="form-group">
          <!-- part for operatorid -->
          <label class="mb-0" for="Operator">Operator: </label>
          <select class="form-control" v-model="operator" required>
            <option
              v-for="operator in operators"
              :key="operator.id"
              :value="operator.id"
            >
              {{ operator.username }}
            </option>
          </select>
        </div>

        <!-- part for Charge-Nr. -->
        <div class="form-group">
          <label class="mb-0" for="BatchNumber">Batch Nummer: </label>
          <input
            type="text"
            class="form-control"
            v-model="batchNumber"
            id="BatchNumber"
            required
          />
        </div>

        <!-- part for Q-Passed-Nr. -->
        <div class="form-group">
          <label class="mb-0" for="QPassedNumber">Q-Passed Nummer: </label>
          <input
            type="text"
            class="form-control"
            v-model="qPassedNumber"
            id="QPassedNumber"
            required
          />
        </div>

        <!-- datePicker for expirydate -->
        <div class="mb-3 form-group">
          <label class="mb-0" for="production">{{ datePickerLabel }}</label>
          <VueDatePicker
            class="b-form-datepicker"
            v-model="expiryDate"
            :enable-time-picker="false"
            required
            >{{ expiryDate }}</VueDatePicker
          >
        </div>

        <!-- part for isGoodEntrance -->
        <div class="mb-2 form-check">
          <input
            class="form-check-input"
            type="checkbox"
            v-model="isGoodEntrance"
            value=""
            id="goodEntrance"
          />
          <label class="form-check-label" for="goodEntrance">
            Wareneingang
          </label>
        </div>

        <!-- part for Peeltest -->
        <div class="form-group">
          <label class="mb-2">Peeltest: </label>
          <div v-for="(peeltest, id) in peeltests" :key="id">
            <input
              type="radio"
              :id="id"
              v-model="selectedPeeltest"
              :value="peeltest.value"
              class="radio-input"
            />
            <label :for="id">{{ peeltest.text }}</label>
          </div>
        </div>

        <button
          type="button"
          class="btn btn-primary mb-3 mt-4"
          @click="submit()"
        >
          Speichern
        </button>
      </form>

      <!-- success alert -->
      <transition name="alert">
        <div
          class="alert alert-success mt-1"
          role="alert"
          v-if="triggerSuccess"
        >
          Der Eintrag wurde gespeichert!
        </div>
      </transition>

      <!-- failed alert -->
      <transition name="alert">
        <div class="alert alert-danger mt-1" role="alert" v-if="triggerError">
          Eintrag konnte nicht gespeichert werden!
        </div>
      </transition>

      <!-- form invalid alert -->
      <transition name="alert">
        <div
          class="alert alert-warning mt-1"
          role="alert"
          v-if="triggerWarning"
        >
          Bitte füllen Sie alle Felder aus!
        </div>
      </transition>
    </div>

    <div class="col-md-9">
      <FluxTable :url="props.tableUrl" :key="componentKey" />
    </div>
  </div>

  <!-- date expired Modal part -->
  <transition name="modal">
    <template v-if="showModal && triggerDateExpired">
      <MyModal :msg="modalMessageExDate" :visible="showModal">
        <template #success>
          <button
            type="button"
            id="success"
            class="btn btn-success"
            @click="handleModalButton(true)"
          >
            Speichern
          </button>
        </template>
        <template #declined>
          <button
            type="button"
            id="declined"
            class="btn btn-warning"
            @click="handleModalButton(false)"
          >
            Abbrechen
          </button>
        </template>
      </MyModal>
    </template>
  </transition>
  <!-- peeltest n.i.O Modal part -->
  <transition name="modal">
    <template v-if="showModal && triggerPeeltest">
      <MyModal :msg="modalMessagePeeltest" :visible="showModal">
        <template #success>
          <button
            type="button"
            id="success"
            class="btn btn-success"
            @click="handleModalButton(true)"
          >
            Speichern
          </button>
        </template>
        <template #declined>
          <button
            type="button"
            id="declined"
            class="btn btn-warning"
            @click="handleModalButton(false)"
          >
            Abbrechen
          </button>
        </template>
      </MyModal>
    </template>
  </transition>
</template>

<script setup>
import { onMounted, ref, defineProps, watch } from "vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import FluxTable from "@/components/flux/FluxTable.vue";
import MyModal from "@/components/helpers/MyModal.vue";
import { instance as axios } from "@/services/axios";

const props = defineProps({
  stationId: {
    type: Number,
    required: true,
  },
  tableUrl: {
    type: String,
    required: true,
  },
});

const getRequestJobs = `/User/GetUsersByJobtype/2`;
const postRequestFluxUrl = `/Material/AddFlux`;
const componentKey = ref(0);
const currentDate = new Date().toISOString();
const productionDate = ref(new Date().toISOString());
const batchNumber = ref();
const qPassedNumber = ref();
const modalMessageExDate =
  "Das Ablaufdatum wurde bereits überschritten. Trotzdem speichern?";
const modalMessagePeeltest = "Der Peeltest ist n.i.O.. Trotzdem speichern? ";

let operator = ref();
let peeltests = ref([
  { id: 1, text: "i.O.", value: true },
  { id: 2, text: "n.i.O.", value: false },
]);
let selectedPeeltest = ref(null);

const expiryDate = ref(new Date().toISOString());

let datePickerLabel = ref("Ablaufdatum: ");
let isGoodEntrance = ref(false);

const operators = ref([]);

// trigger values for alerts
let triggerSuccess = ref(false);
let triggerError = ref(false);
let triggerWarning = ref(false);
let triggerAbort = ref(false);
let triggerDateExpired = ref(false);
let triggerPeeltest = ref(false);

let showModal = ref(false);
let modalResolve;

const handleModal = () => {
  showModal.value = true;
  return new Promise((resolve) => {
    modalResolve = resolve;
  });
};

const handleModalButton = (value) => {
  showModal.value = false;
  modalResolve(value);
};

// watch function for scanner part
watch(batchNumber, (newValue) => {
  if (newValue && newValue.includes("$")) {
    batchNumber.value = newValue.slice(newValue.indexOf("$") + 1);
  }
});
document.addEventListener("keydown", (event) => {
  if (event.key === "$") {
    batchNumber.value = "";
  }
});
watch(expiryDate, (newExpiryDate) => {
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0); // Set time to midnight to compare dates only

  if (newExpiryDate && newExpiryDate < currentDate) {
    triggerDateExpired.value = true;
  } else {
    triggerDateExpired.value = false;
  }
});
watch(selectedPeeltest, (newValue) => {
  triggerPeeltest.value = !newValue;
});

onMounted(() => {
  axios
    .get(getRequestJobs)
    .then(({ data }) => {
      operators.value = data
        .map(({ id, username }) => ({ id, username }))
        .sort((a, b) => a.username.localeCompare(b.username));
    })
    .catch((error) => console.log(error.message));
});

// reset the form after submit it
function resetForm() {
  operator.value = null;
  batchNumber.value = "";
  productionDate.value = null;
  qPassedNumber.value = null;
  selectedPeeltest.value = null;
  componentKey.value += 1;
  document.getElementById("goodEntrance").checked = false;
}

// submit the form for adding a new flux
async function submit() {
  if (
    !document.getElementById("form").checkValidity() ||
    isGoodEntrance.value == null ||
    // productionDate.value == null ||
    operator.value == null ||
    selectedPeeltest.value == null ||
    qPassedNumber.value == null ||
    batchNumber.value == null
  ) {
    //warning alert
    triggerWarning.value = true;
    setTimeout(() => (triggerWarning.value = false), 5000);
    return;
  }
  if (triggerDateExpired.value) {
    const modalResult = await handleModal();
    if (!modalResult) {
      // abort alert
      triggerAbort.value = true;
      setTimeout(() => (triggerAbort.value = false), 5000);
      return;
    }
  } else if (triggerPeeltest.value) {
    const modalResult = await handleModal();
    if (!modalResult) {
      // abort alert
      triggerAbort.value = true;
      setTimeout(() => (triggerAbort.value = false), 5000);
      return;
    }
  }

  // json object for the POST-Request
  let jsonObject = {
    operatorid: operator.value,
    stationid: props.stationId,
    dateInProduction: currentDate,
    fluxBatch: batchNumber.value,
    qPassedNumber: qPassedNumber.value,
    expiryDate: expiryDate.value,
    peeltestPassed: selectedPeeltest.value,
    incomingGoodsInspection: isGoodEntrance.value,
  };

  try {
    const { data } = await axios.post(postRequestFluxUrl, jsonObject);
    console.log("Erfolgreiche Antwort:", data);
    resetForm();
    resetForm();
  } catch (e) {
    console.log("Ein Fehler ist aufgetreten:", e.message);
    setTimeout(() => (triggerError.value = false), 5000);
    resetForm();
  }
  triggerSuccess.value = true;
  setTimeout(() => (triggerSuccess.value = false), 5000);
}
</script>

<style>
.alert-enter-active,
.alert-leave-active,
.modal-enter-active {
  transition: opacity 0.5s ease;
}
.alert-enter-from,
.alert-leave-to,
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

input[type="radio"] {
  transform: scale(1.3);
}
input[type="checkbox"] {
  transform: scale(1.2);
}
.radio-input {
  margin-right: 10px; /* Abstand zwischen dem Radio-Button und dem Label */
}
</style>
